import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useSettingsStore } from "@/shared/stores";
import { calculateBmi } from "@/shared/utils";
import type { GetFunnelIntakeParams } from "../api";
import { funnelIntakeScreensQuery } from "../api";
import type { FunnelStore } from "../stores";
import { useFunnelStore } from "../stores";
import { indexQuestions } from "../utils/intake-screens";

export const funnelIntakeParams = ({
  heightFeet,
  heightInches,
  initialWeight,
  expectedWeight,
  sex,
}: Pick<
  FunnelStore,
  "heightFeet" | "heightInches" | "initialWeight" | "expectedWeight" | "sex"
>): GetFunnelIntakeParams | undefined => {
  const bmi = calculateBmi({
    heightFeet,
    heightInches,
    weightInPounds: initialWeight,
  });

  return bmi && sex
    ? { bmi, sex, weight: initialWeight, goalWeight: expectedWeight }
    : undefined;
};

export const useFunnelIntakeScreens = () => {
  const language = useSettingsStore((state) => state.language);
  const heightFeet = useFunnelStore((state) => state.heightFeet);
  const heightInches = useFunnelStore((state) => state.heightInches);
  const initialWeight = useFunnelStore((state) => state.initialWeight);
  const expectedWeight = useFunnelStore((state) => state.expectedWeight);
  const sex = useFunnelStore((state) => state.sex);

  const params = funnelIntakeParams({
    heightFeet,
    heightInches,
    initialWeight,
    expectedWeight,
    sex,
  });
  const query = useQuery(funnelIntakeScreensQuery(language, params));

  const questionsByKey = useMemo(
    () => indexQuestions(query.data?.steps ?? []),
    [query.data],
  );

  return { params, query, questionsByKey };
};
